<script setup lang="ts">
    /**
     * This component is a vue friendly wrapper around the Docebo Course Player widget.
     * @see: https://developer.docebo.com/docs/embedded-learning-building-blocks-course-player-widget
     */
    import type {
        DsdkEventName,
        DoceboCoursePayload,
        DoceboCoursePlayerType,
        DoceboGlobalSearchFilter,
        DoceboPlaybackPayload,
        DoceboTmDownloadMode,
        DoceboTrainingMaterialsListLoadedPayload,
        DoceboTrainingMaterialStatusChangedPayload,
        DoceboViewChangedPayload,
        DoceboVolumeLevelChangedPayload,
        DoceboWarningShownPayload,
        DoceboWidgetCreatedPayload,
        DoceboWidgetDestroyedPayload,
        DoceboWidgetFullscreenPayload
    } from './DoceboCoursePlayer.types';
    import {
        ensureDoceboCoursePlayerDsdkBridge,
        subscribeDoceboCoursePlayerDsdk
    } from './doceboCoursePlayerDsdkBridge';

    const {
        id,
        courseId,
        type = 'static',
        sources = undefined,
        autoplay,
        showFullscreen,
        showPip,
        showToc,
        forceFullscreen,
        showThumbsInToc,
        showPreview,
        showCourseDeadline,
        tmDownloadMode = undefined,
        globalSearchFilter = undefined,
        noResultTitle = undefined,
        noResultDescription = undefined
    } = defineProps<{
        id: string;
        courseId: string | number;
        type?: DoceboCoursePlayerType;
        sources?: string;

        autoplay?: boolean;
        showFullscreen?: boolean;
        showPip?: boolean;
        showToc?: boolean;
        forceFullscreen?: boolean;
        showThumbsInToc?: boolean;
        showPreview?: boolean;
        showCourseDeadline?: boolean;

        tmDownloadMode?: DoceboTmDownloadMode;
        globalSearchFilter?: DoceboGlobalSearchFilter;
        noResultTitle?: string;
        noResultDescription?: string;
    }>();

    const emit = defineEmits<{
        (e: 'widgetCreated', payload: DoceboWidgetCreatedPayload): void;
        (e: 'widgetDestroyed', payload: DoceboWidgetDestroyedPayload): void;
        (e: 'courseLoaded' | 'courseStatusChanged', payload: DoceboCoursePayload): void;
        (e: 'playbackStarted' | 'playbackPaused', payload: DoceboPlaybackPayload): void;
        (e: 'playbackReset'): void;
        (e: 'volumeLevelChanged', payload: DoceboVolumeLevelChangedPayload): void;
        (e: 'widgetFullscreen', payload: DoceboWidgetFullscreenPayload): void;
        (e: 'viewChanged', payload: DoceboViewChangedPayload): void;
        (e: 'trainingMaterialsListLoaded', payload: DoceboTrainingMaterialsListLoadedPayload): void;
        (e: 'warningShown', payload: DoceboWarningShownPayload): void;
        (e: 'trainingMaterialStatusChanged', payload: DoceboTrainingMaterialStatusChangedPayload): void;
    }>();

    defineOptions({
        inheritAttrs: false
    });

    const isLoadingCourse = ref(false);
    const dsdkLoadTimedOut = ref(false);
    /** Only mount the web component after the kernel is ready and the page-level DSDK bridge is active. */
    const playerMountAllowed = ref(false);

    const { whenReady, hasError } = useDoceboKernel();
    const vueInstanceId = useId();
    const playerDomId = computed(() => {
        const safe = vueInstanceId.replace(/[^a-zA-Z0-9_-]/g, '');
        return `${id}-${safe}`;
    });

    let unsubscribeDsdk: (() => void) | null = null;

    const isLoading = computed(() => !playerMountAllowed.value || isLoadingCourse.value);

    watch(
        () => courseId,
        (newCourseId, oldCourseId) => {
            if (newCourseId !== oldCourseId) {
                isLoadingCourse.value = true;
            }
        },
        { immediate: true }
    );

    const toBoolAttr = (value: boolean | undefined) => {
        if (value === undefined) return undefined;
        return value ? 'true' : 'false';
    };

    const DSDK_EVENT_HANDLERS = {
        widget_created: (payload: unknown) => emit('widgetCreated', payload as DoceboWidgetCreatedPayload),
        widget_destroyed: (payload: unknown) => emit('widgetDestroyed', payload as DoceboWidgetDestroyedPayload),
        course_loaded: (payload: unknown) => emit('courseLoaded', payload as DoceboCoursePayload),
        course_status_changed: (payload: unknown) => emit('courseStatusChanged', payload as DoceboCoursePayload),
        playback_started: (payload: unknown) => emit('playbackStarted', payload as DoceboPlaybackPayload),
        playback_paused: (payload: unknown) => emit('playbackPaused', payload as DoceboPlaybackPayload),
        playback_reset: () => emit('playbackReset'),
        volume_level_changed: (payload: unknown) =>
            emit('volumeLevelChanged', payload as DoceboVolumeLevelChangedPayload),
        widget_fullscreen: (payload: unknown) => emit('widgetFullscreen', payload as DoceboWidgetFullscreenPayload),
        view_changed: (payload: unknown) => emit('viewChanged', payload as DoceboViewChangedPayload),
        training_materials_list_loaded: (payload: unknown) =>
            emit('trainingMaterialsListLoaded', payload as DoceboTrainingMaterialsListLoadedPayload),
        warning_shown: (payload: unknown) => emit('warningShown', payload as DoceboWarningShownPayload),
        training_material_status_changed: (payload: unknown) =>
            emit('trainingMaterialStatusChanged', payload as DoceboTrainingMaterialStatusChangedPayload)
    } satisfies Record<DsdkEventName, (payload: unknown) => void>;

    /**
     * DSDK listeners are invoked either as (emitterId, data) per Docebo docs, or with a single
     * (data) argument depending on SDK build. When only one value is passed, it is the event payload.
     */
    const emitDsdkEvent = (eventName: DsdkEventName, emitterIdOrPayload: unknown, payload?: unknown) => {
        if (eventName === 'course_loaded' || eventName === 'widget_created') {
            isLoadingCourse.value = false;
        }
        const eventPayload = payload !== undefined ? payload : emitterIdOrPayload;
        DSDK_EVENT_HANDLERS[eventName](eventPayload);
    };

    const onBridgeEvent = (eventName: DsdkEventName, emitterIdOrPayload: unknown, payload?: unknown) => {
        emitDsdkEvent(eventName, emitterIdOrPayload, payload);
    };

    let isUnmounted = false;

    onMounted(async () => {
        try {
            await whenReady();
        } catch {
            if (!isUnmounted) dsdkLoadTimedOut.value = true;
            return;
        }
        if (isUnmounted) return;
        if (!ensureDoceboCoursePlayerDsdkBridge()) {
            dsdkLoadTimedOut.value = true;
            return;
        }

        unsubscribeDsdk = subscribeDoceboCoursePlayerDsdk(onBridgeEvent);
        playerMountAllowed.value = true;
    });

    onBeforeUnmount(() => {
        isUnmounted = true;
        unsubscribeDsdk?.();
        unsubscribeDsdk = null;
    });
</script>

<template>
    <div
        v-if="dsdkLoadTimedOut || hasError"
        class="dsdk-error"
        role="alert"
    >
        {{ $t('academy.docebo.dsdkError') }}
    </div>
    <div
        v-else-if="isLoading"
        class="loading"
    >
        <LoadingSpinner />
    </div>
    <!--
            Docebo reads booleans from HTML attributes (e.g. forcefullscreen="false").
            Without .attr, Vue sets a DOM property instead; the string "false" is truthy and forced fullscreen stays on.
        -->
    <dcbo-course-player
        v-if="playerMountAllowed && !dsdkLoadTimedOut"
        v-bind="$attrs"
        :id="playerDomId"
        :courseid="String(courseId)"
        :type="type"
        :sources="sources"
        :autoplay.attr="toBoolAttr(autoplay)"
        :showfullscreen.attr="toBoolAttr(showFullscreen)"
        :showpip.attr="toBoolAttr(showPip)"
        :showtoc.attr="toBoolAttr(showToc)"
        :forcefullscreen.attr="toBoolAttr(forceFullscreen ?? false)"
        :showthumbsintoc.attr="toBoolAttr(showThumbsInToc)"
        :showpreview.attr="toBoolAttr(showPreview)"
        :showcoursedeadline.attr="toBoolAttr(showCourseDeadline)"
        :tmdownloadmode="tmDownloadMode"
        :globalsearchfilter="globalSearchFilter"
        :noresulttitle="noResultTitle"
        :noresultdescription="noResultDescription"
    />
</template>

<style lang="css" scoped>
    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
    }

    .dsdk-error {
        display: flex;
        justify-content: center;
        align-items: center;
        box-sizing: border-box;
        height: 100%;
        width: 100%;
        padding: 1rem;
        text-align: center;
        color: var(--error);
    }
</style>
